import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { Turnstile, useConfig } from '../components/Turnstile';
import { Button } from '../components/ui';
import { PasswordInput } from '../components/fields';
import { useDocumentTitle } from '../lib/hooks';

export function AuthLayout({ title, sub, children, below }: { title: string; sub: ReactNode; children: ReactNode; below?: ReactNode }) {
  useDocumentTitle(title);
  return (
    <div className="auth">
      <div className="box anim-up">
        <div className="brand">
          <div className="logo" />
          <div className="name">Mesa</div>
          <div className="tag">CRM para UAN</div>
        </div>
        <div className="card">
          <h1 style={{ fontSize: 21, fontWeight: 600, letterSpacing: '-.02em', margin: '0 0 5px' }}>{title}</h1>
          <div style={{ fontSize: 13.5, color: 'var(--text-2)', lineHeight: 1.5, marginBottom: 20 }}>{sub}</div>
          {children}
        </div>
        {below && <div style={{ marginTop: 14, textAlign: 'center' }}>{below}</div>}
      </div>
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement> & { label: string }) {
  const { label, ...rest } = props;
  return (
    <label className="field" style={{ cursor: 'default' }}>
      <span className="label">{label}</span>
      {rest.type === 'password' ? <PasswordInput {...rest} /> : <input className={`input ${rest.className ?? ''}`} {...rest} />}
    </label>
  );
}

/** Rotas internas só (evita open redirect via ?next=). */
const safeNext = (n: string | null) => (n && n.startsWith('/') && !n.startsWith('//') ? n : '/');

function useAuthForm() {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [reset, setReset] = useState(0);
  const cfg = useConfig().data;
  const needsCaptcha = !!cfg?.turnstileSiteKey;
  const submit = async (fn: () => Promise<void>) => {
    setError('');
    if (needsCaptcha && !token) return setError('Aguarde a verificação anti-robô.');
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      setError(errMsg(e));
      setReset((x) => x + 1);
    } finally {
      setBusy(false);
    }
  };
  return { error, setError, busy, token, setToken, reset, submit };
}

export function Login() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const f = useAuthForm();
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [senha, setSenha] = useState('');
  const next = safeNext(params.get('next'));

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void f.submit(async () => {
      await api.post('/api/auth/login', { email, senha, turnstileToken: f.token });
      await qc.resetQueries();
      nav(next, { replace: true });
    });
  };

  return (
    <AuthLayout
      title="Entrar"
      sub="Acesse sua plataforma ou as plataformas em que você é parceira."
      below={<Link to="/esqueci" className="link-btn u" style={{ fontSize: 12.5 }}>Esqueci minha senha</Link>}
    >
      <form onSubmit={onSubmit} noValidate>
        <Input label="E-mail" type="email" inputMode="email" autoComplete="email" placeholder="thaisa@nutri.com.br" value={email} onChange={(e) => setEmail(e.target.value)} required />
        <Input label="Senha" type="password" autoComplete="current-password" placeholder="••••••••" value={senha} onChange={(e) => setSenha(e.target.value)} required />
        <Turnstile onToken={f.setToken} resetKey={f.reset} />
        {f.error && <div className="error-box" role="alert">{f.error}</div>}
        <Button variant="dark" size="lg" block type="submit" loading={f.busy} style={{ marginTop: 18 }}>Entrar</Button>
      </form>
      <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
        Ainda não tem conta? <Link to={`/cadastro${params.toString() ? `?${params}` : ''}`} className="link-btn u" style={{ fontSize: 13, fontWeight: 500 }}>Cadastre-se</Link>
      </div>
    </AuthLayout>
  );
}

export function Signup() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const f = useAuthForm();
  const inviteToken = params.get('convite') ?? undefined;
  const shareToken = params.get('compartilhado') ?? undefined;
  const conta = params.get('conta') === 'empresa' ? 'empresa' : 'nutricionista';
  const lockedEmail = params.get('email');
  const [nome, setNome] = useState('');
  const [crn, setCrn] = useState('');
  const [email, setEmail] = useState(lockedEmail ?? '');
  const [senha, setSenha] = useState('');

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (nome.trim().length < 3) return f.setError('Informe seu nome completo.');
    if (conta === 'nutricionista' && !crn.trim()) return f.setError('Informe seu CRN.');
    const min = conta === 'empresa' ? 6 : 8;
    if (senha.length < min) return f.setError(`A senha precisa ter ao menos ${min} caracteres.`);
    void f.submit(async () => {
      const r = await api.post<{ next?: string }>('/api/auth/signup', {
        nome, crn, email, senha, conta, inviteToken, shareToken, turnstileToken: f.token,
      });
      await qc.resetQueries();
      nav(r.next ?? '/', { replace: true });
    });
  };

  const empresa = conta === 'empresa';
  return (
    <AuthLayout
      title={empresa ? 'Criar seu acesso' : 'Criar sua plataforma'}
      sub={empresa ? 'Acompanhe as visitas técnicas e responda às pendências da sua unidade.' : 'Você começa como proprietária e depois convida suas parceiras.'}
    >
      <form onSubmit={onSubmit} noValidate>
        <Input label="Nome completo" autoComplete="name" placeholder={empresa ? 'Marcos Dias' : 'Thaisa Ribeiro'} value={nome} onChange={(e) => setNome(e.target.value)} />
        {!empresa && <Input label="CRN" className="mono" placeholder="CRN-3 41872" value={crn} onChange={(e) => setCrn(e.target.value)} />}
        <Input label="E-mail" type="email" inputMode="email" autoComplete="email" placeholder="voce@empresa.com.br" value={email} onChange={(e) => setEmail(e.target.value)} readOnly={!!lockedEmail} />
        <Input label="Senha" type="password" autoComplete="new-password" placeholder={empresa ? 'Pelo menos 6 letras ou números' : 'Mínimo de 8 caracteres'} value={senha} onChange={(e) => setSenha(e.target.value)} />
        <Turnstile onToken={f.setToken} resetKey={f.reset} />
        {f.error && <div className="error-box" role="alert">{f.error}</div>}
        <Button variant="dark" size="lg" block type="submit" loading={f.busy} style={{ marginTop: 18 }}>Criar conta</Button>
      </form>
      <div style={{ marginTop: 16, textAlign: 'center', fontSize: 13, color: 'var(--text-2)' }}>
        Já tem conta? <Link to={`/entrar${params.toString() ? `?${params}` : ''}`} className="link-btn u" style={{ fontSize: 13, fontWeight: 500 }}>Entrar</Link>
      </div>
    </AuthLayout>
  );
}

export function Forgot() {
  const f = useAuthForm();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  return (
    <AuthLayout title="Recuperar senha" sub="Enviaremos um link para você criar uma nova senha." below={<Link to="/entrar" className="link-btn u" style={{ fontSize: 12.5 }}>Voltar para entrar</Link>}>
      {sent ? (
        <div className="ok-box">Se existir uma conta com <strong>{email}</strong>, o link chega em instantes. Confira também a caixa de spam.</div>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void f.submit(async () => {
              await api.post('/api/auth/forgot', { email, turnstileToken: f.token });
              setSent(true);
            });
          }}
        >
          <Input label="E-mail" type="email" inputMode="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Turnstile onToken={f.setToken} resetKey={f.reset} />
          {f.error && <div className="error-box" role="alert">{f.error}</div>}
          <Button variant="dark" size="lg" block type="submit" loading={f.busy} style={{ marginTop: 18 }}>Enviar link</Button>
        </form>
      )}
    </AuthLayout>
  );
}

export function Reset() {
  const { token } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [senha, setSenha] = useState('');
  const [conf, setConf] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title="Nova senha" sub="Escolha uma senha fácil de lembrar, com pelo menos 6 caracteres.">
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          if (senha.length < 6) return setError('A senha precisa ter ao menos 6 caracteres.');
          if (senha !== conf) return setError('As senhas não conferem.');
          setBusy(true);
          setError('');
          try {
            await api.post('/api/auth/reset', { token, senha });
            await qc.resetQueries();
            nav('/', { replace: true });
          } catch (err) {
            setError(errMsg(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <Input label="Nova senha" type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} />
        <Input label="Repita a senha" type="password" autoComplete="new-password" value={conf} onChange={(e) => setConf(e.target.value)} />
        {error && <div className="error-box" role="alert">{error}</div>}
        <Button variant="dark" size="lg" block type="submit" loading={busy} style={{ marginTop: 18 }}>Salvar e entrar</Button>
      </form>
    </AuthLayout>
  );
}
